import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { defaultConfig, dynamicConfigURL } from 'global/config';

const CONFIG_LOADING_STATE = {
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error'
};

const initState = {
    config: defaultConfig,
    loadingState: CONFIG_LOADING_STATE.LOADING
};

/**
 * @typedef {Object} Config
 * @property {string} apiUrl - The base URL for the API.
 * @property {string} fileApiUrl - The URL for the file API.
 * @property {string} loginApiUrl - The URL for the login API.
 * @property {string} googleMapsApiKey - The API key for Google Maps.
 * @property {string} reCaptchaKey - The API key for reCAPTCHA.
 */

/**
 * @typedef {Object} ConfigState
 * @property {Config} config - The configuration object.
 * @property {string} loadingState - The current loading state.
 */

/**
 * @typedef {Object} ConfigContextProps
 * @property {ConfigState} configSetting - The current configuration settings.
 * @property {function} setConfigSetting - Function to update the configuration settings.
 */

/**
 * @typedef {Object} ConfigProviderProps
 * @property {React.ReactNode} children - The child components to be wrapped by the provider.
 */

const ConfigContext = createContext(initState);
export const useConfigContext = () => useContext(ConfigContext);

export const ConfigProvider = ({ children }) => {
    const [configSetting, setConfigSetting] = useState(initState);
    const { config, loadingState } = configSetting;
    useEffect(() => {
        (async () => {
            try {
                const response = await axios.get(dynamicConfigURL);
                if (response.data instanceof Object) {
                    setConfigSetting({
                        config: response.data,
                        loadingState: CONFIG_LOADING_STATE.READY
                    });
                } else {
                    throw new Error();
                }
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    setConfigSetting((prevState) => ({
                        ...prevState,
                        loadingState: CONFIG_LOADING_STATE.READY
                    }));
                } else {
                    setConfigSetting((prevState) => ({
                        ...prevState,
                        loadingState: CONFIG_LOADING_STATE.ERROR
                    }));
                }
            }
        })();
    }, []);

    if (loadingState === CONFIG_LOADING_STATE.LOADING) {
        return (
            <div className="login-container" />
        );
    }

    if (loadingState === CONFIG_LOADING_STATE.ERROR) {
        return (
            <div>Error while fetching global config</div>
        );
    }

    return (
        <ConfigContext.Provider value={config}>
            {children}
        </ConfigContext.Provider>
    );
};
